<template>
    <!-- Top filter bar -->
    <div class="px-10 shrink overflow-x-auto sticky top-0 z-10 bg-white dark:bg-gray-785 flex items-center justify-between border-b border-keyline-primary" ref="headerWrapper">
        
        <div v-if="selectedTag" class="sticky top-4 z-10 w-full mr-8">
            <div class="py-1 my-[calc(1rem+1px)] flex items-center gap-2">
                <button class="group"
                    @click="selectedTag = null"
                >
                    <BaseSvg
                        class="inline-block max-w-none w-8 h-8 rounded p-1 border border-blue fill-blue group-hover:fill-white group-hover:bg-blue scale-90"
                        name="icon-close"
                    />
                </button>
                {{ $t("Category:") }}
                <span class="font-bold">{{ selectedTag?.name }}</span>
            </div>
        </div>

        <div v-else>
            <AlphabetFilter
                v-model="query.letter"
                :available-letters="availableLetters"
                class="py-1 my-[calc(1rem+1px)]"
            />
        </div>

        <div class="w-auto flex items-center gap-2">
            <button @click.prevent="displaySetting = 'grid'">
                <span>
                    <BaseSvg
                        name="icon-grid-view"
                        :class="
                            displaySetting === 'grid'
                                ? 'opacity-100'
                                : 'opacity-25 at1024:hover:opacity-50'
                        "
                        class="duration-75 w-8 type-primary"
                    />
                </span>
            </button>
            <button @click.prevent="displaySetting = 'rows'">
                <span>
                    <BaseSvg
                        name="icon-list"
                        :class="
                            displaySetting === 'rows'
                                ? 'opacity-100'
                                : 'opacity-25 at1024:hover:opacity-50'
                        "
                        class="duration-75 w-8 type-primary"
                    />
                </span>
            </button>
        </div>
    </div>

    <!-- Manufacturer cards -->
    <div class="p-10 pt-6">
        <ul
            class="w-full"
            :class="
                displaySetting === 'grid'
                    ? 'grid gap-8 grid-cols-1 at768:grid-cols-2 at1200:grid-cols-3'
                    : ''
            "
        >
            <template v-for="manufacturer in filteredManufacturers">
                <li v-if="marketingAssetUrl(manufacturer.logo?.external_id)">
                    <ManufacturerCard
                        :manufacturer="manufacturer"
                        :display-setting="displaySetting"
                    />
                </li>
            </template>
        </ul>
    </div>
    
</template>

<script setup>
import {
    AlphabetFilter,
    BaseSvg,
    ManufacturerCard,
} from "@/Components";
import {computed, inject, onMounted, reactive, ref, watch} from "vue";
import {useAssetTransforms} from "@/Composables";
import {router, usePage} from "@inertiajs/vue3";
const { marketingAssetUrl } = useAssetTransforms();
const props = defineProps(["manufacturers", "tags", "availableLetters", 'externalNav', 'selectedTag']);


const displaySetting = ref("grid");
const route = inject("route");

const query = reactive(usePage().props.query ?? {});

const selectedTag = computed({
    get: () => {
        return props.selectedTag;
    },
    set: (tag) => {
        // Update the local query to trigger router update
        query.tag = tag?.name || null;
        if(tag){
            query.letter = null;
        }
    },
});

const appIsScrolled = ref(false);
const headerWrapper = ref();

onMounted(() => {
    const scrollContainer = document.getElementById('main-scroll-container');
    
    if (scrollContainer) {
        scrollContainer.addEventListener('scroll', () => {
            if (scrollContainer.scrollTop > 20) {
                appIsScrolled.value = true;
                headerWrapper.value?.classList.add('shadow-lg');
            } else {
                appIsScrolled.value = false;
                headerWrapper.value?.classList.remove('shadow-lg');
            }
        }, { passive: true });
    }
});

watch(query, (newQuery) => {
    router.get(route(route().current()),
        query,
        {
            replace: true,
            preserveScroll: true,
            preserveState: true,
            preserveQuery: false,
            only: ["referer", "query","flash"],
        }
    );
});

const filteredManufacturers = computed(() => {
    let data = props.manufacturers;

    if (selectedTag.value) {
        return data.filter((manufacturer) =>
            manufacturer.tag_ids?.includes(selectedTag.value.id)
        );
    }

    if (query.letter) {
        return data.filter((manufacturer) =>
            manufacturer.name?.startsWith(query.letter.toLowerCase()) || manufacturer.name?.startsWith(query.letter.toUpperCase())
        );
    }

    return data;
});

const navigateToShow = (slug) => {
    router.visit(route('web.catalog.manufacturers.show', slug))
};
</script>
